import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const OrderUpdatesContext = createContext();

export const useOrderUpdates = () => {
  return useContext(OrderUpdatesContext);
};

// How many of the customer's most recently updated orders to listen to. Each
// is one billed read when the listener starts; a status change is one more.
const LIVE_ORDER_LIMIT = 10;

const STATUS_MESSAGES = {
  preparing: 'is being prepared',
  'on-the-way': 'is on the way',
  delivered: 'has been delivered',
  cancelled: 'was cancelled'
};

// Hands an order status change straight to whichever page cares (e.g.
// OrderHistory) the moment it happens. Two sources feed it:
//   - a push notification arriving while the app is open
//     (NotificationForegroundListener), and
//   - a live Firestore listener on the signed-in customer's own
//     order_status documents (below) - which works even when push
//     notifications aren't enabled.
// The same change often arrives from both, so publishUpdate drops a repeat.
export const OrderUpdatesProvider = ({ children }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [lastUpdate, setLastUpdate] = useState(null);
  // True while the live listener is healthy. OrderHistory polls only when it
  // isn't, so a listener failure degrades to the old behaviour, not to a
  // screen that silently stops updating.
  const [liveConnected, setLiveConnected] = useState(false);
  const lastPublishedRef = useRef(null);

  // Returns true if this was news. A repeat of the last update (same order and
  // status within 30s) is dropped. Push payloads carry no paymentStatus, so it
  // is only compared when both sides have one.
  const publishUpdate = useCallback(({ orderId, status, paymentStatus }) => {
    if (!orderId) return false;
    const now = Date.now();
    const last = lastPublishedRef.current;
    const isRepeat =
      last &&
      now - last.at < 30000 &&
      last.orderId === orderId &&
      last.status === status &&
      (paymentStatus === undefined || last.paymentStatus === undefined || last.paymentStatus === paymentStatus);
    if (isRepeat) {
      if (paymentStatus !== undefined) last.paymentStatus = paymentStatus;
      return false;
    }
    lastPublishedRef.current = { orderId, status, paymentStatus, at: now };
    setLastUpdate({ orderId, status, paymentStatus, receivedAt: now });
    return true;
  }, []);

  const uid = user?.uid || null;

  useEffect(() => {
    if (!uid) {
      setLiveConnected(false);
      return undefined;
    }

    // Must filter on userId: the rules only let a customer read their own
    // order_status docs, and reject any query that could return someone
    // else's. Needs the (userId ASC, updatedAt DESC) composite index.
    const liveQuery = query(
      collection(db, 'order_status'),
      where('userId', '==', uid),
      orderBy('updatedAt', 'desc'),
      limit(LIVE_ORDER_LIMIT)
    );

    let isFirstSnapshot = true;
    const unsubscribe = onSnapshot(
      liveQuery,
      (snapshot) => {
        setLiveConnected(true);
        // The first snapshot is the current state, which the page already
        // loaded from the API - only changes after it are news.
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          return;
        }
        snapshot.docChanges().forEach((change) => {
          // 'removed' = an older order dropping out of the most-recent window
          if (change.type === 'removed') return;
          const { status, paymentStatus } = change.doc.data();
          const isNew = publishUpdate({ orderId: change.doc.id, status, paymentStatus });

          // A push notification already shows a toast for this; only add one
          // when push isn't enabled in this browser.
          const pushEnabled = typeof Notification !== 'undefined' && Notification.permission === 'granted';
          if (isNew && !pushEnabled && change.type === 'modified' && STATUS_MESSAGES[status]) {
            const ref = change.doc.id.slice(-6).toUpperCase();
            addToast(`Your order #${ref} ${STATUS_MESSAGES[status]}`, status === 'cancelled' ? 'error' : 'info', 5000);
          }
        });
      },
      (error) => {
        console.warn('Live order updates unavailable:', error.code || error.message);
        setLiveConnected(false);
      }
    );

    return () => {
      unsubscribe();
      setLiveConnected(false);
    };
    // addToast is recreated on every ToastProvider render; depending on it
    // would tear the listener down and re-bill its initial reads each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, publishUpdate]);

  const value = {
    lastUpdate,
    publishUpdate,
    liveConnected
  };

  return (
    <OrderUpdatesContext.Provider value={value}>
      {children}
    </OrderUpdatesContext.Provider>
  );
};
